import { createApi } from "@reduxjs/toolkit/query/react";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import {
    IncidentDetailResponse,
    IncidentDispositionPayload,
    IncidentDispositionResponse,
    IncidentLayoutResponse,
    IncidentListingPayload,
    IncidentRecord,
    IncidentReviewAdminRequest,
    IncidentReviewAdminResponse,
    IncidentReviewConfigPayload,
} from "../../pages/admins/incident/type";
import { API_ENDPOINTS } from "./endpoints";
import { API_METHODS } from "./constants";
import { ListingResponse } from "../types/Listing";

export const IncidentAPIs = createApi({
    reducerPath: "incidentApi",
    tagTypes: ['IncidentReview', 'IncidentDetail'],
    baseQuery: (args, api, extraOptions) =>
        baseQueryWithAuthHandling(
            appBaseQuery.withAuthJson,
            args,
            api,
            extraOptions
        ),
    endpoints: (builder) => ({

        // get all events and selected incidents
        getIncidentConfig: builder.query<IncidentReviewAdminResponse,IncidentReviewConfigPayload>({
            query:(params:IncidentReviewConfigPayload) => {
                return {
                    url: API_ENDPOINTS.INCIDENTS.INCIDENT_CONFIG,
                    method: API_METHODS.GET,
                    params: params
                };
            },
            transformErrorResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformResponse: (response: IncidentReviewAdminResponse) => {
                return response;
            },
            providesTags: ['IncidentReview']
        }),

        // to modify the selected incident list
        modifyIncidentConfig: builder.mutation<IncidentReviewAdminResponse, IncidentReviewAdminRequest>({
            query: (statusCodes: IncidentReviewAdminRequest) => {
                return {
                    url: API_ENDPOINTS.INCIDENTS.INCIDENT_CONFIG,
                    method: API_METHODS.PUT,
                    body: statusCodes
                };
            },
            transformErrorResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformResponse: (response: IncidentReviewAdminResponse) => {
                return response;
            },
            invalidatesTags: ['IncidentReview'],
        }),

        getIncidentListing: builder.query<ListingResponse<IncidentRecord[]>, IncidentListingPayload>({
            query: (params:IncidentListingPayload) => {
                return {
                    url: API_ENDPOINTS.INCIDENTS.INCIDENT_LISTING,
                    method: API_METHODS.GET,
                    params: params
                };
            },
            transformErrorResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformResponse: (response: ListingResponse<IncidentRecord[]>) => {
                return response;
            },
            providesTags: ['IncidentReview']
        }),

        getIncidentLayout: builder.query<IncidentLayoutResponse, void>({
            query: () => {
                return {
                    url: API_ENDPOINTS.INCIDENTS.INCIDENT_LAYOUT,
                    method: API_METHODS.GET,
                };
            },
            transformErrorResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformResponse: (response: IncidentLayoutResponse) => {
                return response;
            },
        }),

        getIncidentDetail: builder.query<IncidentDetailResponse, string | number>({
            query: (recordid: string | number) => {
                return {
                    url: API_ENDPOINTS.INCIDENTS.INCIDENT_DETAIL(recordid),
                    method: API_METHODS.GET,
                };
            },
            transformErrorResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformResponse: (response: IncidentDetailResponse) => {
                return response;
            },
            providesTags: (result, error, recordid) => [{ type: 'IncidentDetail', id: recordid }],
        }),

        setIncidentDisposition: builder.mutation<IncidentDispositionResponse, IncidentDispositionPayload>({
            query: (payload: IncidentDispositionPayload) => {
                return {
                    url: API_ENDPOINTS.INCIDENTS.INCIDENT_DISPOSITION,
                    method: API_METHODS.PUT,
                    body: payload,
                };
            },
            transformErrorResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformResponse: (response: IncidentDispositionResponse) => {
                return response;
            },
            invalidatesTags: ['IncidentReview'],
        }),

        getExportIncidentListing:  builder.query<any, IncidentListingPayload>({
            query: (params:IncidentListingPayload) => {
                return {
                    url: API_ENDPOINTS.INCIDENTS.INCIDENT_LISTING,
                    method: API_METHODS.GET,
                    params: params,
                    responseHandler: "blob",
                };
            },
            transformErrorResponse(baseQueryReturnValue) {
                return baseQueryReturnValue;
            },
            transformResponse: (response: any) => {
                return response;
            },
        }),

    })
})

export const {
    useGetIncidentConfigQuery,
    useModifyIncidentConfigMutation,
    useLazyGetIncidentConfigQuery,
    useGetIncidentListingQuery,
    useLazyGetExportIncidentListingQuery,
    useGetIncidentLayoutQuery,
    useGetIncidentDetailQuery,
    useSetIncidentDispositionMutation,
} = IncidentAPIs;